import React, { useState, useEffect } from 'react';
import { 
  getVersionLocalizations, 
  updateVersionLocalization,
  createVersionLocalization,
  deleteVersionLocalization
} from '../../services/api';

// Common locales for iOS apps (Apple App Store Connect format)
const AVAILABLE_LOCALES = [
  { code: 'ar-SA', name: 'Arabic' },
  { code: 'ca', name: 'Catalan' },
  { code: 'zh-Hans', name: 'Chinese (Simplified)' },
  { code: 'zh-Hant', name: 'Chinese (Traditional)' },
  { code: 'hr', name: 'Croatian' },
  { code: 'cs', name: 'Czech' },
  { code: 'da', name: 'Danish' },
  { code: 'nl-NL', name: 'Dutch' },
  { code: 'en-AU', name: 'English (Australia)' },
  { code: 'en-CA', name: 'English (Canada)' },
  { code: 'en-GB', name: 'English (U.K.)' },
  { code: 'en-US', name: 'English (U.S.)' },
  { code: 'fi', name: 'Finnish' },
  { code: 'fr-FR', name: 'French' },
  { code: 'fr-CA', name: 'French (Canada)' },
  { code: 'de-DE', name: 'German' },
  { code: 'el', name: 'Greek' },
  { code: 'he', name: 'Hebrew' },
  { code: 'hi', name: 'Hindi' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'id', name: 'Indonesian' },
  { code: 'it', name: 'Italian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'ms', name: 'Malay' },
  { code: 'no', name: 'Norwegian' },
  { code: 'pl', name: 'Polish' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)' },
  { code: 'pt-PT', name: 'Portuguese (Portugal)' },
  { code: 'ro', name: 'Romanian' },
  { code: 'ru', name: 'Russian' },
  { code: 'sk', name: 'Slovak' },
  { code: 'es-MX', name: 'Spanish (Mexico)' },
  { code: 'es-ES', name: 'Spanish (Spain)' },
  { code: 'sv', name: 'Swedish' },
  { code: 'th', name: 'Thai' },
  { code: 'tr', name: 'Turkish' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'vi', name: 'Vietnamese' },
];

function LocalizationManager({ versionId, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localizations, setLocalizations] = useState([]);
  const [selectedLocale, setSelectedLocale] = useState('');
  const [showAddLocale, setShowAddLocale] = useState(false);
  const [newLocale, setNewLocale] = useState('');
  const [formData, setFormData] = useState({
    description: '',
    keywords: '',
    marketingUrl: '',
    promotionalText: '',
    supportUrl: '',
    whatsNew: '',
  });

  useEffect(() => {
    if (versionId) {
      loadLocalizations();
    }
  }, [versionId]);

  const loadLocalizations = async () => {
    setLoading(true);
    try {
      const response = await getVersionLocalizations(versionId);
      if (response.data.success) {
        setLocalizations(response.data.localizations);
        
        // Use the first available localization instead of hardcoding en-US
        if (response.data.localizations.length > 0) {
          const firstLocalization = response.data.localizations[0];
          setSelectedLocale(firstLocalization.attributes.locale);
          updateFormData(firstLocalization);
        }
      }
    } catch (err) {
      console.error('Failed to load localizations:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (localization) => {
    setFormData({
      description: localization.attributes.description || '',
      keywords: localization.attributes.keywords || '',
      marketingUrl: localization.attributes.marketingUrl || '',
      promotionalText: localization.attributes.promotionalText || '',
      supportUrl: localization.attributes.supportUrl || '',
      whatsNew: localization.attributes.whatsNew || '',
    });
  };

  const handleLocaleChange = (locale) => {
    setSelectedLocale(locale);
    const localization = localizations.find(loc => loc.attributes.locale === locale);
    if (localization) {
      updateFormData(localization);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const localization = localizations.find(loc => loc.attributes.locale === selectedLocale);
      if (localization) {
        await updateVersionLocalization(localization.id, formData);
        alert('Localization updated successfully!');
        onUpdate();
      }
    } catch (err) {
      alert('Failed to update localization: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAddLocale = async () => {
    if (!newLocale) {
      alert('Please select a locale to add');
      return;
    }

    // Check if locale already exists
    if (localizations.some(loc => loc.attributes.locale === newLocale)) {
      alert('This locale already exists');
      return;
    }

    setLoading(true);
    try {
      const response = await createVersionLocalization(versionId, newLocale);
      if (response.data.success) {
        alert('Localization added successfully!');
        setShowAddLocale(false);
        setNewLocale('');
        await loadLocalizations();
        onUpdate();
      }
    } catch (err) {
      alert('Failed to add localization: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLocale = async () => {
    if (localizations.length === 1) {
      alert('Cannot delete the last localization. At least one localization is required.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the ${selectedLocale} localization?`)) {
      return;
    }

    setLoading(true);
    try {
      const localization = localizations.find(loc => loc.attributes.locale === selectedLocale);
      if (localization) {
        await deleteVersionLocalization(localization.id);
        alert('Localization deleted successfully!');
        await loadLocalizations();
        onUpdate();
      }
    } catch (err) {
      alert('Failed to delete localization: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const getAvailableLocales = () => {
    const existingLocales = localizations.map(loc => loc.attributes.locale);
    return AVAILABLE_LOCALES.filter(locale => !existingLocales.includes(locale.code));
  };

  if (loading) {
    return (
      <div className="loading-inline">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="form-group">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <label className="form-label" style={{ margin: 0 }}>Language/Region</label>
          <button
            className="btn btn-secondary"
            onClick={() => setShowAddLocale(!showAddLocale)}
            style={{ fontSize: '13px', padding: '6px 12px' }}
          >
            {showAddLocale ? '✕ Cancel' : '+ Add Locale'}
          </button>
        </div>

        {showAddLocale && (
          <div className="form-card" style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label className="form-label" style={{ fontSize: '13px' }}>Select Locale to Add</label>
                <select
                  className="form-select"
                  value={newLocale}
                  onChange={(e) => setNewLocale(e.target.value)}
                >
                  <option value="">Choose a locale...</option>
                  {getAvailableLocales().map(locale => (
                    <option key={locale.code} value={locale.code}>
                      {locale.name} ({locale.code})
                    </option>
                  ))}
                </select>
              </div>
              <button
                className="btn btn-primary"
                onClick={handleAddLocale}
                disabled={!newLocale || loading}
                style={{ whiteSpace: 'nowrap' }}
              >
                {loading ? 'Adding...' : 'Add Localization'}
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select
            className="form-select"
            value={selectedLocale}
            onChange={(e) => handleLocaleChange(e.target.value)}
            style={{ flex: 1 }}
          >
            {localizations.map((loc, index) => {
              const localeInfo = AVAILABLE_LOCALES.find(l => l.code === loc.attributes.locale);
              const displayName = localeInfo ? `${localeInfo.name} (${loc.attributes.locale})` : loc.attributes.locale;
              const isPrimary = index === 0;
              return (
                <option key={loc.id} value={loc.attributes.locale}>
                  {isPrimary ? '⭐ ' : ''}{displayName}{isPrimary ? ' - Primary' : ''}
                </option>
              );
            })}
          </select>
          {localizations.length > 1 && (
            <button
              className="btn btn-error"
              onClick={handleDeleteLocale}
              style={{ whiteSpace: 'nowrap' }}
              title="Delete this localization"
            >
              🗑️ Delete
            </button>
          )}
        </div>
        <small className="text-secondary" style={{ display: 'block', marginTop: '8px' }}>
          {localizations.length} localization{localizations.length !== 1 ? 's' : ''} configured • First locale is the primary/default
        </small>
      </div>

      <div className="form-group">
        <label className="form-label">Promotional Text</label>
        <textarea
          className="form-textarea"
          value={formData.promotionalText}
          onChange={(e) => setFormData({ ...formData, promotionalText: e.target.value })}
          maxLength={170}
          placeholder="Promotional text appears at the top of your app's description"
        />
        <small className="text-secondary">{formData.promotionalText.length}/170 characters</small>
      </div>

      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          className="form-textarea"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          maxLength={4000}
          rows={8}
          placeholder="Describe your app's features and functionality"
        />
        <small className="text-secondary">{formData.description.length}/4000 characters</small>
      </div>

      <div className="form-group">
        <label className="form-label">Keywords</label>
        <input
          type="text"
          className="form-input"
          value={formData.keywords}
          onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
          maxLength={100}
          placeholder="Separate keywords with commas"
        />
        <small className="text-secondary">{formData.keywords.length}/100 characters</small>
      </div>

      <div className="grid grid-2">
        <div className="form-group">
          <label className="form-label">Support URL</label>
          <input
            type="url"
            className="form-input"
            placeholder="https://example.com/support"
            value={formData.supportUrl}
            onChange={(e) => setFormData({ ...formData, supportUrl: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Marketing URL (Optional)</label>
          <input
            type="url"
            className="form-input"
            placeholder="https://example.com/marketing"
            value={formData.marketingUrl}
            onChange={(e) => setFormData({ ...formData, marketingUrl: e.target.value })}
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">What's New in This Version</label>
        <textarea
          className="form-textarea"
          value={formData.whatsNew}
          onChange={(e) => setFormData({ ...formData, whatsNew: e.target.value })}
          maxLength={4000}
          rows={6}
          placeholder="Describe what's new in this version"
        />
        <small className="text-secondary">{formData.whatsNew.length}/4000 characters</small>
      </div>

      <button
        className="btn btn-primary"
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'Saving...' : 'Save Localization'}
      </button>
    </div>
  );
}

export default LocalizationManager;
